import {
  NO_CAPABILITIES,
  type TranslationCapabilities,
  type TranslationProvider,
  type TranslationSession,
  type TranslationSessionOptions,
} from './translation-provider';
import {
  TRANSLATION_SAMPLE_RATE,
  decodeServerMessage,
  type ProtocolCapabilities,
  type StartMessage,
} from './translation-protocol';

export interface WebSocketTranslationConfig {
  /** `wss://…` in any deployment; see the mixed-content note below. */
  endpoint: string;
  /**
   * Correlates the socket with the consultation in the logs (§9.1).
   *
   * Read lazily because a session id does not exist until a call starts, and
   * this provider is constructed at application bootstrap.
   *
   * Must not close over `SessionService`: that service injects
   * `TranslationService`, which injects this provider's token, so reaching back
   * for it closes a dependency cycle — and Angular resolves a cycle by handing
   * back a half-built instance whose fields are still undefined. That presented
   * as `TypeError: sessionId is not a function` at the first caption.
   */
  sessionId?: () => string | null;
  token?: () => string | null;
}

/**
 * Read a callback that may be absent or, through a DI cycle, not yet a
 * function.
 *
 * Correlation is diagnostic, not functional: losing it should degrade the logs,
 * never take captions down. Returning null here keeps a misconfiguration to a
 * harder-to-trace log line instead of a failed consultation feature.
 */
function callOptional(fn: (() => string | null) | undefined): string | null {
  return typeof fn === 'function' ? fn() : null;
}

/** Path to the tap worklet, served verbatim from `src/assets`. */
const TAP_WORKLET_URL = 'assets/worklets/translation-tap-processor.js';
const TAP_PROCESSOR = 'translation-tap-processor';

/**
 * How long to wait for the server's `ready` before giving up.
 *
 * A socket that opens but never acknowledges is the failure a bare `onopen`
 * cannot see — the TCP connection succeeds through a proxy that then drops the
 * upgrade, and without this the UI would sit in "starting" forever.
 */
const READY_TIMEOUT_MS = 5_000;

/**
 * Translation over a WebSocket to our own server (§8.1).
 *
 * The browser recognises nothing. It downsamples the microphone on the audio
 * thread, ships int16 PCM, and renders the text that comes back. That is what
 * makes the feature identical on both sides: `AudioContext` and `WebSocket` are
 * the only platform APIs involved, and neither is a speech API the Android
 * WebView might lack.
 *
 * The socket is deliberately **not** Jitsi's. If it dies, the call is untouched
 * — the "strictly additive" rule of §8 holding at the transport layer rather
 * than only in the code above it.
 */
export class WebSocketTranslationProvider implements TranslationProvider {
  readonly name = 'websocket';

  /**
   * Learned from the server's `ready`, not configured here.
   *
   * Empty until the first session connects, which the UI must read as *unknown*
   * rather than *none* (§8.3).
   */
  private lastCapabilities: TranslationCapabilities = NO_CAPABILITIES;

  constructor(private readonly config: WebSocketTranslationConfig) {}

  isAvailable(): boolean {
    return this.config.endpoint.length > 0;
  }

  capabilities(): TranslationCapabilities {
    return this.lastCapabilities;
  }

  async start(options: TranslationSessionOptions): Promise<TranslationSession> {
    const socket = new WebSocket(this.config.endpoint);
    socket.binaryType = 'arraybuffer';

    const session = new WebSocketSession(socket, options, (capabilities) => {
      this.lastCapabilities = capabilities;
    });

    try {
      await session.open({
        type: 'start',
        sourceLanguage: options.sourceLanguage,
        targetLanguage: options.targetLanguage,
        sampleRate: TRANSLATION_SAMPLE_RATE,
        sessionId: callOptional(this.config.sessionId) ?? 'unknown',
        ...(callOptional(this.config.token)
          ? { token: callOptional(this.config.token)! }
          : {}),
      });
    } catch (error) {
      // Release the socket and the audio graph before rethrowing. A failed
      // start that leaves either behind would hold the microphone clone open
      // for the rest of the call.
      await session.stop();
      throw error;
    }

    return session;
  }
}

/**
 * One socket, one audio graph, one consultation-side session.
 *
 * Kept separate from the provider so that everything with a lifetime is in one
 * place: `stop()` is the single path that releases the graph, the context and
 * the socket, and it is idempotent because teardown can arrive from either end
 * — the user toggling off, the stethoscope taking over, or the server erroring.
 */
class WebSocketSession implements TranslationSession {
  private context: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private tap: AudioWorkletNode | null = null;
  /** Silent pull path — see `startTap`. */
  private sink: GainNode | null = null;
  private stopped = false;

  constructor(
    private readonly socket: WebSocket,
    private readonly options: TranslationSessionOptions,
    private readonly onCapabilities: (value: TranslationCapabilities) => void,
  ) {}

  /**
   * Connect, handshake, then start sending audio.
   *
   * Audio only flows after `ready`, so a server that rejects the language or the
   * token never receives speech it cannot use.
   */
  async open(start: StartMessage): Promise<void> {
    await this.waitForOpen();
    this.socket.send(JSON.stringify(start));
    await this.waitForReady();
    await this.startTap();

    // Handlers for the running session, installed only once the handshake has
    // succeeded — before this, `waitForReady` owns the socket's events.
    this.socket.onmessage = (event) => this.onMessage(event);
    this.socket.onerror = () => this.fail('the translation service is unreachable');
    this.socket.onclose = (event) => {
      // A clean close after `stop()` is expected and must not raise a notice.
      if (this.stopped || event.wasClean) return;
      this.fail('the translation service disconnected');
    };
  }

  async stop(): Promise<void> {
    if (this.stopped) return;
    this.stopped = true;

    // Tear the graph down before the socket, so no frame is posted into a
    // closing connection.
    this.tap?.port.close();
    this.tap?.disconnect();
    this.tap = null;

    this.sink?.disconnect();
    this.sink = null;

    this.source?.disconnect();
    this.source = null;

    if (this.context) {
      try {
        await this.context.close();
      } catch {
        // An already-closed context throws; nothing here can act on it, and the
        // graph is unreachable either way.
      }
      this.context = null;
    }

    if (this.socket.readyState === WebSocket.OPEN) {
      // Lets the server flush a partial utterance rather than dropping it.
      try {
        this.socket.send(JSON.stringify({ type: 'stop' }));
      } catch {
        // The socket died between the readyState check and the send. The close
        // below is what matters.
      }
    }

    this.socket.onmessage = null;
    this.socket.onerror = null;
    this.socket.onclose = null;
    this.socket.close();
  }

  private waitForOpen(): Promise<void> {
    if (this.socket.readyState === WebSocket.OPEN) return Promise.resolve();

    return new Promise((resolve, reject) => {
      this.socket.onopen = () => resolve();
      this.socket.onerror = () =>
        reject(
          // The browser gives script no reason for a failed handshake — that is
          // a deliberate security boundary, not a gap to work around. So this
          // reports the endpoint and lists the causes rather than asserting one:
          // an earlier version guessed "must be wss://" and sent someone
          // looking at TLS when the server was simply not running.
          new Error(
            `could not reach the translation service at ${this.socket.url} — ` +
              'check that it is running, and that the URL is wss:// when the ' +
              'page is served over HTTPS',
          ),
        );
    });
  }

  private waitForReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        reject(new Error('the translation service did not respond'));
      }, READY_TIMEOUT_MS);

      this.socket.onmessage = (event) => {
        if (typeof event.data !== 'string') return;

        const result = decodeServerMessage(event.data);
        if (!result.ok) {
          window.clearTimeout(timer);
          reject(new Error(`malformed handshake: ${result.reason}`));
          return;
        }

        if (result.message.type === 'error') {
          window.clearTimeout(timer);
          reject(new Error(result.message.message));
          return;
        }

        if (result.message.type === 'ready') {
          window.clearTimeout(timer);
          this.onCapabilities(toCapabilities(result.message.capabilities));
          resolve();
        }

        // A segment before `ready` is out of order; ignore it and keep waiting
        // rather than treating the handshake as complete.
      };
    });
  }

  /**
   * Build the audio graph.
   *
   * The context runs at the target rate where the browser allows it, so the
   * worklet's decimation ratio is 1:1 and costs nothing. Where it does not, the
   * worklet resamples from whatever rate it was given — `sampleRate` is read
   * inside the processor, so this is a performance detail and not a correctness
   * one.
   */
  private async startTap(): Promise<void> {
    const context = new AudioContext({ sampleRate: TRANSLATION_SAMPLE_RATE });
    this.context = context;

    await context.audioWorklet.addModule(TAP_WORKLET_URL);

    // A context created while the page is backgrounded starts suspended, and a
    // suspended context runs no worklet — silent captions with no error.
    if (context.state === 'suspended') {
      await context.resume();
    }

    this.source = context.createMediaStreamSource(
      new MediaStream([this.options.track]),
    );

    // One output, declared but never connected to the destination.
    //
    // A node with `numberOfOutputs: 0` is not reliably pulled by Chromium's
    // graph — with no path toward the destination there is nothing to drive it,
    // and `process()` may simply never run. That produced an open socket, a
    // healthy-looking session and no audio at all.
    //
    // The isolation §8 requires is preserved by the output being left
    // unconnected and the processor writing nothing to it: there is still no
    // route from this node to what the far side hears.
    this.tap = new AudioWorkletNode(context, TAP_PROCESSOR, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });

    this.tap.port.onmessage = (event) => {
      const data = event.data;
      if (!data) return;

      if (data.type === 'stats') {
        // Proves the worklet is running even when nothing is being said, which
        // is what separates "the tap is dead" from "the room is quiet" (§9.3).
        this.options.onStats?.({
          framesPosted: data.framesPosted,
          silentQuanta: data.silentQuanta,
          sampleRate: data.sampleRate,
        });
        return;
      }

      if (data.type !== 'frame') return;
      if (this.stopped || this.socket.readyState !== WebSocket.OPEN) return;

      this.socket.send(data.buffer as ArrayBuffer);
    };

    this.source.connect(this.tap);

    // A zero-gain sink gives the graph a reason to pull the tap. Silent by
    // construction — the gain is 0 and the processor emits nothing — so this
    // adds a pull path without adding an audio path.
    const sink = context.createGain();
    sink.gain.value = 0;
    this.tap.connect(sink);
    sink.connect(context.destination);
    this.sink = sink;
  }

  private onMessage(event: MessageEvent): void {
    if (typeof event.data !== 'string') return;

    const result = decodeServerMessage(event.data);
    if (!result.ok) {
      // Dropped rather than surfaced: one malformed frame is a bug to find in
      // the logs, not a reason to take captions down mid-consultation.
      return;
    }

    const message = result.message;

    if (message.type === 'error') {
      this.fail(message.message);
      return;
    }

    if (message.type === 'segment') {
      this.options.onSegment({
        id: message.id,
        sourceText: message.sourceText,
        translatedText: message.translatedText,
        sourceLanguage: message.sourceLanguage,
        targetLanguage: message.targetLanguage,
        final: message.final,
      });
      return;
    }

    if (message.type === 'ready') {
      // A server may re-declare capabilities if its engine changed under it.
      this.onCapabilities(toCapabilities(message.capabilities));
    }
  }

  /**
   * Report and shut down.
   *
   * Translation is strictly additive, so a failure stops captions and says so
   * while the consultation continues untouched.
   */
  private fail(message: string): void {
    if (this.stopped) return;
    this.options.onError(message);
    void this.stop();
  }
}

function toCapabilities(value: ProtocolCapabilities): TranslationCapabilities {
  return {
    sourceLanguages: value.sourceLanguages,
    targetLanguages: value.targetLanguages,
    emitsPartials: value.emitsPartials,
  };
}
